'use client';

import type { ComponentProps } from 'react';
import { cn } from '@/lib/utils/cn';
import { Link } from '@/i18n/navigation';
import { Card, CardContent, CardDescription, CardTitle } from '@/components/ui/primitives/card';
import { FadeIn } from '@/components/animations/primitives/fade-in';
import { getAuthorCardUi } from '@/lib/config/ui';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/primitives/avatar';
import { Button } from '@/components/ui/primitives/button';
import { ArrowRight } from 'lucide-react';

export interface Author {
  name: string;
  role?: string | null;
  avatar?: string | null;
  bio?: string | null;
  link?: string | null;
}

interface AuthorCardProps {
  author?: Author | null;
  tinaField?: string;
  className?: string;
  locale: string;
}

export function AuthorCard({ author, tinaField, className, locale }: AuthorCardProps) {
  const ui = getAuthorCardUi(locale);

  if (!author?.name) {
    return null;
  }

  const initials = author.name
    .split(' ')
    .map(part => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <FadeIn>
      <Card data-tina-field={tinaField} className={cn('gap-3', className)}>
        <CardContent className="flex items-start gap-3 pt-4">
          <Avatar size="lg" className="rounded-md">
            {author.avatar ? <AvatarImage src={author.avatar} alt={author.name} /> : null}
            <AvatarFallback className="rounded-md bg-primary text-primary-foreground">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col gap-1">
            <CardTitle as="h3" className="text-base">
              {author.name}
            </CardTitle>
            {author.role ? (
              <CardDescription className="text-xs">{author.role}</CardDescription>
            ) : null}
          </div>
        </CardContent>
        {author.bio ? (
          <CardContent className="pt-0 text-sm text-muted-foreground">{author.bio}</CardContent>
        ) : null}
        {author.link ? (
          <CardContent className="pt-0">
            <Button
              asChild
              variant="ghost"
              size="sm"
              className="h-auto p-0 font-sans text-sm text-foreground hover:text-primary"
            >
              {author.link.startsWith('/') ? (
                <Link
                  href={author.link as ComponentProps<typeof Link>['href']}
                  className="inline-flex items-center gap-1"
                >
                  {ui.cta}
                  <ArrowRight className="size-3" />
                </Link>
              ) : (
                <a
                  href={author.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1"
                >
                  {ui.cta}
                  <ArrowRight className="size-3" />
                </a>
              )}
            </Button>
          </CardContent>
        ) : null}
      </Card>
    </FadeIn>
  );
}
